import { DEPOSIT_LIKE_TYPES, UI_GROUP } from '../shared/bonusFeedUtils';
import { resolveBonusImageUrl, resolveBonusPlaceholderSrc } from '../shared/bonusImage';
import {
  resolveBonusInstanceDisplayStatus,
  resolveBonusStatus,
  resolveBonusType,
  resolveCountdownMs,
  resolveDisplayCurrency,
  resolveFinanceForCurrency,
  resolveFinishedExpiryDate,
  resolveWageringProps,
} from '../shared/bonusProps';

const BONUS_RECEIVED = 'bonus_received';
const BONUS_MIN_DEPOSIT = 'bonus_min_dep';
// Freespins are counted in spins, not currency -> the "Bonus Received" amount uses this unit.
const FREESPIN_UNIT = 'FS';
// FreeSpinSubType.DEPOSIT from @atlas/bonuses - deposit freespins follow the deposit-bonus
// flow on the list card (DEPOSIT -> ACTIVATE -> CANCEL, Min Deposit subtitle, progress bar).
const FREESPIN_DEPOSIT_SUBTYPE = 'DEPOSIT';

const isDepositFreespinRow = (kind, row) =>
  kind === 'freespin' && row.freeSpinSubType === FREESPIN_DEPOSIT_SUBTYPE;

/**
 * Label + amount shown under the bonus title, per UI spec:
 *   Available Deposit / Reload Deposit -> 'Min Deposit: {amount}'
 *   Available No-Deposit / Unlocked / Activated (non-freespin) -> 'Bonus Received: {amount}'
 *   Freespins (outside available deposit) -> 'Bonus Received: {count} FS' (mirrors the detail header)
 *   Finished bonuses -> no subtitle
 */
const resolveSubtitle = ({ finance, formatter, kind, row, uiGroup }) => {
  if (uiGroup === UI_GROUP.FINISHED) {
    return { showSubtitle: false };
  }
  const isDepositFreespin = isDepositFreespinRow(kind, row);
  const isDepositLike = isDepositFreespin || DEPOSIT_LIKE_TYPES.has(row.type);
  if (uiGroup === UI_GROUP.AVAILABLE && isDepositLike) {
    return {
      subtitleAmount: formatter(
        isDepositFreespin
          ? (row.freeSpinMinAmountsDeposit?.[row.currency] ?? 0)
          : (finance?.minDeposit ?? 0),
      ),
      subtitleLabel: BONUS_MIN_DEPOSIT,
    };
  }

  // Freespins are received as a spin COUNT -> "{numberOfSpins} FS"; bonuses show the money amount.
  if (kind === 'freespin') {
    return {
      subtitleAmount: row.freeSpin?.numberOfSpins ?? 0,
      subtitleCurrency: FREESPIN_UNIT,
      subtitleLabel: BONUS_RECEIVED,
    };
  }

  const amount = row.bonusAmount ?? finance?.maxBonusAmount ?? 0;
  return {
    subtitleAmount: formatter(amount),
    subtitleLabel: BONUS_RECEIVED,
  };
};
export const buildProfileBonusCardProps = (
  item,
  { onActivate, onCancel, onOpenDeposit, onOpenDetails, onPlay, wallet },
  formatter = value => value,
) => {
  const { kind, row, uiGroup } = item;
  const isFinished = uiGroup === UI_GROUP.FINISHED;

  const finance = kind === 'bonus' ? resolveFinanceForCurrency(row, wallet?.currency) : null;
  const displayCurrency = resolveDisplayCurrency(finance, row, wallet);
  const subtitle = resolveSubtitle({
    finance,
    formatter,
    kind,
    row,
    uiGroup,
  });
  const wageringProps = resolveWageringProps(row, kind, uiGroup, displayCurrency, formatter);
  const bonusType = resolveBonusType(row);
  const isFreeSpin = kind === 'freespin';
  const isDepositFreespin = isDepositFreespinRow(kind, row);
  // Deposit / reload-deposit bonuses AND deposit freespins drive the deposit flow
  // (DEPOSIT/ACTIVATE/CANCEL); no-deposit bonuses and no-deposit freespins show ACTIVATE/PLAY.
  const isDepositLike = (kind === 'bonus' && DEPOSIT_LIKE_TYPES.has(row.type)) || isDepositFreespin;

  const name = row.name || row.freeSpin?.name;
  const nameHighlighted = row.nameHighlighted ?? row.freeSpin?.nameHighlighted ?? null;

  return {
    bonusStatus:
      kind === 'bonus' ? resolveBonusInstanceDisplayStatus(row) : resolveBonusStatus(row),
    bonusType,
    countdownEndMs: resolveCountdownMs(row, kind, uiGroup, isFinished),
    currency: displayCurrency ? `${displayCurrency} ` : '',
    expiryDate: isFinished ? resolveFinishedExpiryDate(row) : '—',
    imageUrl: resolveBonusImageUrl(row.imageLink),
    isDepositLike,
    isFreeSpin,
    maxBonus: finance?.maxBonusAmount ?? row.bonusAmount ?? 0,
    minDeposit: isDepositFreespin
      ? (row.freeSpinMinAmountsDeposit?.[row.currency] ?? 0)
      : (finance?.minDeposit ?? 0),
    name,
    nameHighlighted,
    onActivate: () => onActivate?.(item),
    onCancel: () => onCancel?.(item),
    onDeposit: () => onOpenDeposit(row),
    onInfoClick: () => onOpenDetails(item),
    onPlay: () => onPlay?.(item),
    placeholderImageUrl: resolveBonusPlaceholderSrc({ bonusType, isFreeSpin }),
    title: row.name || row.freeSpin?.name,
    ...subtitle,
    ...wageringProps,
  };
};
